import React, { FC, useMemo, useRef } from 'react'

import clsx from 'clsx'
import { useHoverDirty } from 'react-use'
import styled, { css } from 'styled-components'

import { CircleIconButton } from '@style-port/roov-ui-components-react'
import {
  Tooltip,
  Txt,
  Icon,
  LoadingIcon,
  PdfBorderIcon,
  VideoBorderIcon,
  ImageBorderIcon,
} from 'src/components/atoms'
import { CommonContentAssetNoImage, CommonContentItemBase } from 'src/components/molecules'

import { useGetOgpImage } from 'src/hooks'
import { CommonContentAssetContentType, CommonContentAssetEntity } from 'src/types'

const Container = styled.div`
  position: relative;
`

const AssetWrapper = styled.div`
  width: 11.43rem;
  height: 100%;
  flex-shrink: 0;
  position: relative;
`
const NonbleWrapper = styled.div`
  border-radius: 0.57rem 0 0 0;
  position: absolute;
  top: 0;
  overflow: hidden;
`
const Nonble = styled.div<{ contentType: CommonContentAssetContentType }>`
  width: 1.8rem;
  aspect-ratio: 1;
`

const AssetIconBase = css`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`
const AssetIconWrapper = styled.div`
  ${AssetIconBase};
  z-index: 2;
`
const UploadIconWrapper = styled.div`
  ${AssetIconBase};
  z-index: 3;
`

type Props = {
  contentNumber: number
  asset: CommonContentAssetEntity
  isUploading: boolean
  onClickUpload: () => void
  className?: string
  children?: React.ReactNode
  canEdit?: boolean
}

export const CommonContentUploadedBase: FC<Props> = ({
  contentNumber,
  asset,
  isUploading,
  onClickUpload,
  className,
  children,
  canEdit,
}) => {
  const containerReference = useRef(null)
  const isContainerHovering = useHoverDirty(containerReference)
  const { loading, url: ogpImageUrl } = useGetOgpImage({
    fromUrl: asset.externalLink,
    skip: !!asset.thumbUrl, // すでにサムネイルがある場合はOGP画像取得クエリをスキップする
  })
  const thumbUrl = useMemo(() => asset.thumbUrl ?? ogpImageUrl, [asset.thumbUrl, ogpImageUrl])

  return (
    <div className={className} ref={containerReference}>
      <Container>
        <CommonContentItemBase showShadow className="tw-flex">
          <AssetWrapper>
            <NonbleWrapper>
              <Nonble
                contentType={asset.contentType}
                className={clsx(
                  'tw-centering',
                  asset.contentType === 'empty'
                    ? 'tw-bg-cautionYellow-default'
                    : 'tw-bg-standardText-secondary'
                )}
              >
                {asset.contentType === 'empty' ? (
                  <Icon
                    size="--r0"
                    path="eye-off/soft/line"
                    className="tw-text-inlineText-primary"
                  />
                ) : (
                  <Txt
                    sizeType="xs"
                    className="tw-text-inlineText-primary tw-font-bold"
                    fontType="alphabet"
                  >
                    {contentNumber}
                  </Txt>
                )}
              </Nonble>
            </NonbleWrapper>

            {!loading && asset.contentType === 'link' && !thumbUrl ? (
              <CommonContentAssetNoImage className="tw-w-full tw-h-full" />
            ) : (
              <>
                {thumbUrl && (
                  <img src={thumbUrl} alt="" className="tw-w-full tw-h-full tw-object-contain" />
                )}

                <AssetIconWrapper className="tw-centering">
                  {asset.contentType === 'pdf' && <PdfBorderIcon className="tw-w-s2" />}
                  {asset.contentType === 'video' && <VideoBorderIcon className="tw-w-s2" />}
                  {asset.contentType === 'img' && <ImageBorderIcon className="tw-w-s2" />}
                </AssetIconWrapper>
                {canEdit && (
                  <UploadIconWrapper className="tw-centering">
                    {isUploading ? (
                      <LoadingIcon className="tw-w-fit tw-text-3xl" />
                    ) : (
                      <Tooltip
                        placement="top"
                        content={
                          <div className="tw-flex tw-items-center">
                            <Txt sizeType="s">
                              JPG, PNG, GIF形式 : 6MBまで
                              <br />
                              PDF形式 : 30MBまで
                              <br />
                              MP4形式 : 300MBまで
                            </Txt>
                          </div>
                        }
                        showIcon={false}
                      >
                        <div
                          className={`tw-text-standardText-secondary tw-flex ${
                            isContainerHovering || asset.contentType === 'empty'
                              ? 'tw-visible'
                              : 'tw-invisible'
                          }`}
                        >
                          <CircleIconButton
                            className="tw-outline-none"
                            buttonType="inline-primary"
                            onClick={onClickUpload}
                          >
                            <Icon size="--r1" path="upload/soft/line" />
                          </CircleIconButton>
                        </div>
                      </Tooltip>
                    )}
                  </UploadIconWrapper>
                )}
              </>
            )}
          </AssetWrapper>
          <div className="tw-p-s-2 tw-bg-surface-primary tw-w-full">{children}</div>
        </CommonContentItemBase>
      </Container>
    </div>
  )
}
